import { QuickIntroPromptLease } from './QuickIntroPromptLease';
import { makeAutoObservable, runInAction } from 'mobx';
import type { WelcomePlaylistVideo, WelcomeVideoConfig } from '@/core/domain/entities/WelcomeVideo';
import { nextWelcomeVideo, welcomeCooldownElapsed } from '@/core/domain/entities/WelcomeVideoHistory';
import { mergePlayedRanges } from '@/core/domain/entities/playedRanges';
import type { IQuickIntroRepository, QuickIntroAdmission, QuickIntroState, QuickIntroVideoState } from '@/core/domain/repositories/IQuickIntroRepository';
import type { QuickIntroBrowserHistory, QuickIntroOutboxItem } from '@/core/infrastructure/services/QuickIntroBrowserHistory';

function videoState(state: QuickIntroState, id: string): QuickIntroVideoState {
  let video = state.videos.find(item => item.video_id === id);
  if (!video) {
    video = { video_id: id, appearances: 0, watched: false, retired: false, progress: [], played_versions: [] };
    state.videos.push(video);
  }
  return video;
}

export class QuickIntroHistoryStore {
  accountId: string | null = null;
  state: QuickIntroState | null = null;
  hydrated = false;
  loading = false;
  unavailable = false;
  private generation = 0;
  private pendingAdmission: QuickIntroAdmission | null = null;
  private pendingDisplay: { videoId: string; mediaVersion: string; appearance: number } | null = null;
  private accountHoldDeadline = 0;
  private readonly prompt: QuickIntroPromptLease;
  private playedThisIdentity = new Set<string>();

  constructor(private readonly browser: QuickIntroBrowserHistory, private readonly repository?: IQuickIntroRepository,
    private readonly now: () => number = Date.now,
    private readonly monotonicNow: () => number = () => performance.now()) {
    this.prompt = new QuickIntroPromptLease(repository, monotonicNow);
    makeAutoObservable(this);
  }

  private accept(state: QuickIntroState, accountId: string | null, generation: number): void {
    if (generation !== this.generation || accountId !== this.accountId) return;
    if (state.account_id !== accountId || !Array.isArray(state.videos) || typeof state.opted_out !== 'boolean') throw new Error('Invalid Quick Intro history');
    const hold = state.automatic_hold_until === null ? 0 : Date.parse(state.automatic_hold_until);
    const serverTime = state.server_time ? Date.parse(state.server_time) : NaN;
    const remaining = hold === 0 ? 0 : Number.isFinite(hold) && Number.isFinite(serverTime)
      ? Math.max(0, hold - serverTime) : Infinity;
    runInAction(() => {
      this.accountHoldDeadline = accountId ? this.monotonicNow() + remaining : 0;
      this.state = state; this.hydrated = true; this.unavailable = false; });
  }

  async load(accountId: string | null, videos: WelcomePlaylistVideo[]): Promise<void> {
    this.releasePrompt();
    const abandonedAdmission = this.pendingAdmission;
    const abandonedAccount = this.accountId;
    const generation = ++this.generation;
    this.loading = true;
    if (accountId !== this.accountId) this.playedThisIdentity.clear();
    this.accountId = accountId;
    this.state = null;
    this.hydrated = false;
    this.pendingAdmission = null;
    this.pendingDisplay = null;
    this.accountHoldDeadline = 0;
    try {
      await this.browser.exclusive(async () => {
        // A new identity/config can arrive after admission but before the dialog
        // mounts. Preserve that never-shown cancellation under its original owner.
        if (abandonedAdmission && abandonedAccount) this.browser.appendOutbox(abandonedAccount, {
          id: crypto.randomUUID(), action: 'cancel',
          body: { expected_account_id: abandonedAccount, token: abandonedAdmission.token },
        });
        if (generation !== this.generation) return;
        if (accountId) {
          await this.flush(accountId, generation);
          if (!this.repository) throw new Error('History unavailable');
          this.accept(await this.repository.getQuickIntroState(), accountId, generation);
        } else this.accept(this.browser.readGuest(videos), null, generation);
      });
    } catch { if (generation === this.generation) runInAction(() => { this.unavailable = true; }); }
    finally { if (generation === this.generation) runInAction(() => { this.loading = false; }); }
  }

  private async flush(accountId: string, generation: number): Promise<void> {
    if (!this.repository) throw new Error('History unavailable');
    for (const item of this.browser.readOutbox(accountId)) {
      if (generation !== this.generation || accountId !== this.accountId) throw new Error('Identity changed');
      // The durable outbox identity also upgrades queued pre-receipt manual opens.
      // Retries use the same event ID even after a reload or device clock change.
      const body = item.action === 'activity' && item.body.manual_open
        ? { ...item.body, event_id: item.id } : item.body;
      const state = await this.repository.updateQuickIntro(item.action, body);
      if (state.account_id !== accountId) throw new Error('History identity mismatch');
      this.browser.removeOutbox(accountId, item.id);
      this.accept(state, accountId, generation);
    }
  }

  hasWatched(id: string): boolean { return Boolean(this.state?.videos.find(video => video.video_id === id)?.watched); }
  hasPlayed(id: string, version: string): boolean {
    return this.playedThisIdentity.has(`${id}:${version}`) || Boolean(this.state?.videos.find(video => video.video_id === id)?.played_versions.includes(version));
  }

  /** Avoid configuration requests for visits/history that already prohibit invitations. */
  canAttempt(config: WelcomeVideoConfig, videos: WelcomePlaylistVideo[]): boolean {
    if (!this.hydrated || this.unavailable || !config.automatic_enabled || (config.automatic_interval_hours === 0 && config.invitation_protocol !== 2)) return false;
    try {
      if (this.browser.deviceSuppressed()) return false;
      const state = this.accountId ? this.state : this.browser.readGuest(videos);
      if (!state || state.opted_out) return false;
      return Boolean(nextWelcomeVideo(videos, config.main_video_id ?? null,
        Object.fromEntries(state.videos.map(video => [video.video_id, video]))));
    } catch { this.unavailable = true; return false; }
  }

  isCoolingDown(config: WelcomeVideoConfig): boolean {
    try {
      if (this.browser.deviceBlocked(this.now(), config.automatic_interval_hours)) return true;
      if (this.accountId) return this.monotonicNow() < this.accountHoldDeadline;
      const state = this.browser.readGuest([]);
      return !welcomeCooldownElapsed(state.last_opened_at === null ? null : Date.parse(state.last_opened_at),
        this.now(), config.automatic_interval_hours);
    } catch { this.unavailable = true; return true; }
  }

  async admit(config: WelcomeVideoConfig, videos: WelcomePlaylistVideo[], canOpen: () => boolean,
    open: (id: string, appearance: number) => void, expire: () => void = () => {}): Promise<boolean> {
    if (!this.hydrated || this.unavailable || !config.automatic_enabled || (config.automatic_interval_hours === 0 && config.invitation_protocol !== 2)) return false;
    const generation = this.generation;
    const accountId = this.accountId;
    let releaseBrowser: (() => void) | null = null;
    try {
      releaseBrowser = await this.browser.acquirePrompt();
      if (!releaseBrowser) return false;
      return await this.browser.exclusive(async () => {
        if (generation !== this.generation || !canOpen() || this.browser.deviceBlocked(this.now(), config.automatic_interval_hours)) return false;
        let state: QuickIntroState;
        let admission: QuickIntroAdmission | null = null;
        let selected: WelcomePlaylistVideo | undefined;
        let appearance = 0;
        let admissionRequestedAt = 0;
        const deviceState = this.browser.readGuest(videos);
        if (accountId) {
          await this.flush(accountId, generation);
          if (!this.repository || generation !== this.generation || accountId !== this.accountId ||
            this.state?.opted_out || this.monotonicNow() < this.accountHoldDeadline) return false;
          const currentIds = new Set(videos.map(video => video.id));
          const deviceVideos = deviceState.videos.filter(video => currentIds.has(video.video_id));
          admissionRequestedAt = this.monotonicNow();
          const result = await this.repository.reserveQuickIntro(accountId, config.playlist_revision!, {
            excluded_video_ids: deviceVideos.filter(video => video.watched || video.retired).map(video => video.video_id),
            device_appearances: Object.fromEntries(deviceVideos.map(video => [video.video_id, video.appearances])),
          }, config.invitation_protocol === 2 ? 2 : undefined);
          if (result.state.account_id !== accountId) throw new Error('History identity mismatch');
          state = result.state;
          admission = result.admission;
          this.accept(state, accountId, generation);
          if (!admission) return false;
          selected = videos.find(video => video.id === admission!.video_id && video.media_version === admission!.media_version);
          appearance = admission.appearance_number;
        } else {
          state = deviceState;
          if (state.opted_out || !welcomeCooldownElapsed(state.last_opened_at === null ? null : Date.parse(state.last_opened_at), this.now(), config.automatic_interval_hours)) return false;
          selected = nextWelcomeVideo(videos, config.main_video_id ?? null,
            Object.fromEntries(state.videos.map(video => [video.video_id, video])));
          if (selected) appearance = videoState(state, selected.id).appearances + 1;
        }
        // The server grants fifteen seconds after reservation. Measuring from request
        // start is conservative and independent of the device wall-clock offset.
        const leaseDeadline = admission?.prompt_expires_at != null ? QuickIntroPromptLease.deadline({
          server_time: state.server_time ?? '', prompt_expires_at: admission.prompt_expires_at, released: false,
        }, admissionRequestedAt) : null;
        const leaseAllowed = !accountId || (leaseDeadline !== null
          ? Number.isFinite(leaseDeadline) && leaseDeadline > this.monotonicNow()
          : config.automatic_interval_hours !== 0);
        const allowed = leaseAllowed && selected && generation === this.generation && accountId === this.accountId && canOpen() &&
          !this.browser.deviceBlocked(this.now(), config.automatic_interval_hours) && (!admission || (this.monotonicNow() - admissionRequestedAt >= 0 && this.monotonicNow() - admissionRequestedAt < 15000));
        if (!allowed) {
          if (accountId && admission) {
            this.browser.appendOutbox(accountId, { id: crypto.randomUUID(), action: 'cancel',
              body: { expected_account_id: accountId, token: admission.token } });
            if (generation === this.generation && accountId === this.accountId) await this.flush(accountId, generation);
          }
          return false;
        }
        // Reserve this visit conservatively before opening, but record an actual
        // appearance only when the matching dialog confirms it mounted.
        this.browser.markOpened(this.now());
        this.prompt.start(releaseBrowser!, expire, accountId && admission && leaseDeadline !== null ? {
          accountId, token: admission.token, deadline: leaseDeadline,
          release: () => this.releaseAccountPrompt(accountId, admission!.token),
        } : undefined);
        releaseBrowser = null;
        runInAction(() => {
          this.state = state;
          this.pendingAdmission = admission;
          this.pendingDisplay = { videoId: selected!.id, mediaVersion: selected!.media_version, appearance };
          open(selected!.id, appearance);
        });
        return true;
      });
    } catch { this.releasePrompt(); if (generation === this.generation) runInAction(() => { this.unavailable = true; }); return false; }
    finally { releaseBrowser?.(); }
  }

  releasePrompt(): void { this.prompt.release(); }
  checkPrompt(): void { this.prompt.check(); }

  private releaseAccountPrompt(accountId: string, token: string): void {
    // A prompt lease is temporary control state, not durable viewing history.
    // Keep cleanup out of the v1 outbox so an outage or rollback cannot block
    // coverage/opt-out receipts. Token/account checks and server expiry bound loss.
    void this.repository?.updateQuickIntroPrompt(accountId, token, 'release').catch(() => {});
  }

  /** Cancel a known-never-visible opening without attributing it to a new identity. */
  abandon(): void {
    this.releasePrompt();
    const admission = this.pendingAdmission;
    const accountId = this.accountId;
    const generation = this.generation;
    this.pendingAdmission = null;
    this.pendingDisplay = null;
    if (!admission || !accountId) return;
    void this.browser.exclusive(async () => {
      this.browser.appendOutbox(accountId, { id: crypto.randomUUID(), action: 'cancel',
        body: { expected_account_id: accountId, token: admission.token } });
      if (generation === this.generation && accountId === this.accountId) await this.flush(accountId, generation);
    }).catch(() => { if (generation === this.generation) runInAction(() => { this.unavailable = true; }); });
  }

  /** Called after the player dialog is mounted, never when its timer is scheduled. */
  async visible(video: WelcomePlaylistVideo, automatic: boolean): Promise<void> {
    const accountId = this.accountId;
    const generation = this.generation;
    const admission = this.pendingAdmission;
    const display = this.pendingDisplay;
    if (automatic && (!display || display.videoId !== video.id || display.mediaVersion !== video.media_version)) return;
    this.pendingAdmission = null;
    this.pendingDisplay = null;
    const openedAt = new Date(this.now()).toISOString();
    const manualReceipt: QuickIntroOutboxItem | null = accountId && !automatic ? {
      id: crypto.randomUUID(), action: 'activity', body: {
        expected_account_id: accountId, video_id: video.id, media_version: video.media_version, manual_open: true,
      },
    } : null;
    let receiptQueued = false;
    try {
      await this.browser.exclusive(async () => {
        this.browser.markOpened(Date.parse(openedAt));
        // Account manual receipts do not depend on unrelated guest history.
        const deviceState = automatic || !accountId ? this.browser.readGuest([video]) : null;
        if (deviceState) {
          if (automatic && display) {
            const deviceVideo = videoState(deviceState, video.id);
            deviceVideo.appearances = Math.max(deviceVideo.appearances, display.appearance);
          }
          deviceState.last_opened_at = openedAt;
          this.browser.saveGuest(deviceState);
        }
        if (accountId) {
          if (automatic && admission) this.browser.appendOutbox(accountId, {
            id: crypto.randomUUID(), action: 'shown', body: { expected_account_id: accountId, token: admission.token },
          });
          else if (manualReceipt) {
            this.browser.appendOutbox(accountId, manualReceipt);
            receiptQueued = true;
          }
          if (generation === this.generation) await this.flush(accountId, generation);
        } else {
          this.accept(deviceState!, null, generation);
        }
      });
    } catch {
      // Manual viewing still establishes the account quiet period when browser
      // locking/storage is unavailable. A partially saved receipt keeps its ID.
      if (manualReceipt && !receiptQueued && this.repository &&
        generation === this.generation && accountId === this.accountId) {
        try {
          const state = await this.repository.updateQuickIntro('activity', {
            ...manualReceipt.body, event_id: manualReceipt.id,
          });
          this.accept(state, accountId, generation);
        } catch { /* Durable receipts, when available, retry on the next load. */ }
      }
      // Account receipt success cannot establish safe browser admission state.
      if (generation === this.generation) runInAction(() => { this.unavailable = true; });
    }
  }

  async progress(video: WelcomePlaylistVideo, ranges: [number, number][], duration: number,
    facts: { started?: true; reached_end?: true } = {}): Promise<void> {
    const accountId = this.accountId;
    const generation = this.generation;
    const validDuration = Number.isFinite(duration) && duration > 0 && duration <= 86400;
    if (facts.started) this.playedThisIdentity.add(`${video.id}:${video.media_version}`);
    const merged = validDuration ? mergePlayedRanges(ranges, duration) : [];
    try {
      await this.browser.exclusive(async () => {
        if (accountId) {
          this.browser.appendOutbox(accountId, { id: crypto.randomUUID(), action: 'activity', body: {
            expected_account_id: accountId, video_id: video.id, media_version: video.media_version,
            ...(validDuration ? { ranges: merged, duration_seconds: duration } : {}), ...facts,
          } });
          if (generation === this.generation) {
            await this.flush(accountId, generation);
            this.accept(await this.repository!.getQuickIntroState(), accountId, generation);
          }
        } else {
          const state = this.browser.readGuest([video]);
          const previous = videoState(state, video.id);
          if (facts.started && !previous.played_versions.includes(video.media_version)) previous.played_versions.push(video.media_version);
          if (facts.reached_end) previous.retired = true;
          if (validDuration) {
            let progress = previous.progress.find(item => item.media_version === video.media_version);
            if (!progress) { progress = { media_version: video.media_version, duration_seconds: duration, ranges: [] }; previous.progress.push(progress); }
            progress.ranges = mergePlayedRanges([...progress.ranges, ...merged], duration);
            progress.duration_seconds = duration;
            if (progress.ranges.reduce((sum, [start, end]) => sum + end - start, 0) / duration >= 0.95) previous.watched = true;
          }
          this.browser.saveGuest(state);
          this.accept(state, null, generation);
        }
      });
    } catch { if (generation === this.generation) runInAction(() => { this.unavailable = true; }); }
  }

  async suppress(): Promise<void> {
    this.releasePrompt();
    const accountId = this.accountId;
    const generation = this.generation;
    this.unavailable = true;
    if (this.state) this.state.opted_out = true;
    try {
      this.browser.suppress();
      if (accountId) await this.browser.exclusive(async () => {
        this.browser.appendOutbox(accountId, { id: crypto.randomUUID(), action: 'opt-out', body: { expected_account_id: accountId } });
        await this.flush(accountId, generation);
      });
    } catch { /* Explicit device suppression stays in memory if storage/network fails. */ }
  }
}
